<template>
  <div class="bg-white dark:bg-dark-900 overflow-hidden rounded-lg shadow-lg">
    <!-- Header with Search and Filter -->
    <div class="p-4 border-b border-gray-100 dark:border-dark-800">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:flex gap-4 md:justify-between items-center">
        <div>
          <h2 class="text-3xl font-bold text-gray-800 dark:text-gray-200">
            {{ title }}
          </h2>
          <p class="text-base text-gray-500 dark:text-gray-400 mt-1">
            {{ caption }}
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:flex gap-3">
          <div class="relative">
            <input type="text" v-model="searchQuery" placeholder="Cari Meja..."
              class="pl-10 pr-4 py-2 border dark:border-dark-700 rounded-lg bg-gray-50 dark:bg-dark-800 text-gray-900 dark:text-gray-100" />
            <MagnifyingGlassIcon class="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
          <select v-model="statusFilter"
            class="px-4 py-2 border dark:border-dark-700 rounded-lg bg-gray-50 dark:bg-dark-800 text-gray-900 dark:text-gray-100">
            <option value="">Semua Status</option>
            <option v-for="status in tableStatuses" :key="status" :value="status">
              {{ formatStatus(status) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table Grid -->
    <div class="max-h-[600px] overflow-y-auto p-4">
      <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
        <div v-for="table in filteredTables" :key="table.id" class="relative group cursor-pointer"
          @click="handleTableClick(table)">
          <div
            class="p-2 flex flex-col items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg"
            :class="[
              getTableStyles(table.status),
              table.id === selectedTableId &&
              allowedModal.includes(table.status) &&
              'ring-4 ring-primary-500 dark:ring-primary-400',
            ]">
            <h3 class="text-2xl font-bold" :class="[
              getTableTextStyles(table.status),
              table.id === selectedTableId &&
              allowedModal.includes(table.status) &&
              'text-primary-700 dark:text-primary-300',
            ]">
              {{ table.name }}
            </h3>

            <div class="flex flex-col items-center gap-1">
              <span class="text-[10px] sm:text-xs lg:text-sm font-medium px-2 py-0.5 rounded-full" :class="[
                getStatusBadgeStyles(table.status),
                table.id === selectedTableId &&
                allowedModal.includes(table.status) &&
                'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300',
              ]">
                {{ formatStatus(table.status) }}
              </span>

              <span class="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                <UserGroupIcon class="h-4 w-4" />
                {{ table.capacity }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { MagnifyingGlassIcon, UserGroupIcon } from "@heroicons/vue/24/outline";

export default {
  name: "NewTransactionCardTable",
  components: {
    MagnifyingGlassIcon,
    UserGroupIcon
  },
  props: {
    tables: {
      type: Array,
      required: true,
    },
    allowedModal: {
      type: Array,
      default: () => ["available", "ready"],
    },
    selectedTableId: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      default: "Daftar Meja",
    },
    caption: {
      type: String,
      default: "Pilih meja untuk membuat transaksi baru",
    },
  },
  emits: ["table-selected"],
  data() {
    return {
      searchQuery: "",
      statusFilter: "",
      tableStatuses: [
        "available",
        "unavailable"
      ],
    };
  },
  computed: {
    ...mapGetters("tables", ["allTables"]),
    ...mapGetters("cash_registers", ["allCashRegisters"]),
    ...mapGetters("sales_orders", ["allSalesOrders"]),
    relevantCashRegister() {
      return this.allCashRegisters?.find((cr) => !cr.time_end);
    },
    filteredTables() {
      if (!this.allTables || !this.allSalesOrders || !this.relevantCashRegister) return [];

      return this.allTables.map((table) => {
        const tableCopy = { ...table };

        // For new transactions, we only want to show available tables
        // Check if table has an active order in current cash register
        const salesOrder = this.getRelevantSalesOrder(tableCopy.id);
        if (salesOrder !== undefined) {
          tableCopy.status = 'unavailable';
        }

        const matchesSearch = tableCopy.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());
        const matchesStatus =
          !this.statusFilter || tableCopy.status === this.statusFilter;

        // For new transactions, we only want to show available or ready tables
        const isAvailableForNewTransaction = true;
          // tableCopy.status === 'available' ||
          // tableCopy.status === 'ready';

        return (matchesSearch && matchesStatus && isAvailableForNewTransaction) ? tableCopy : null;
      }).filter(Boolean);
    },
  },
  created() {
    this.$store.dispatch("tables/fetchTables", { limit: 100 });
    this.$store.dispatch("cash_registers/fetchCashRegisters");
    if (this.relevantCashRegister) {
      this.$store.dispatch("sales_orders/fetchSalesOrders", {
        cash_register_id: this.relevantCashRegister.id,
        limit: 100,
      });
    }
  },
  methods: {
    handleTableClick(table) {
      if (table.status === 'available' || table.status === 'ready') {
        this.$emit('table-selected', table);
      }
    },
    formatStatus(status) {
      return status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    getTableStyles(status) {
      const styles = {
        available:
          "bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500",
        ready:
          "bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500",
        reserved: "bg-primary-50 dark:bg-primary-900/30 border-2 border-primary-500",
        not_ordered: "bg-gray-50 dark:bg-gray-800 border-2 border-gray-500",
        maintenance:
          "bg-orange-50 dark:bg-orange-900/30 border-2 border-orange-500",
        unavailable: "bg-red-50 dark:bg-red-900/30 border-2 border-red-500",
      };
      return styles[status] || styles["unavailable"];
    },
    getTableTextStyles(status) {
      const styles = {
        available: "text-emerald-700 dark:text-emerald-300",
        ready: "text-emerald-700 dark:text-emerald-300",
        reserved: "text-primary-700 dark:text-primary-300",
        not_ordered: "text-gray-700 dark:text-gray-300",
        maintenance: "text-orange-700 dark:text-orange-300",
        unavailable: "text-red-700 dark:text-red-300",
      };
      return styles[status] || styles["unavailable"];
    },
    getStatusBadgeStyles(status) {
      const styles = {
        available:
          "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300",
        ready:
          "bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300",
        reserved:
          "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300",
        not_ordered:
          "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300",
        maintenance:
          "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300",
        unavailable:
          "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300",
      };
      return styles[status] || styles["unavailable"];
    },
    getRelevantSalesOrder(tableId) {
      if (!this.relevantCashRegister) return undefined;

      return this.allSalesOrders?.find(
        (so) =>
          so.table_id == tableId &&
          !["paid", "canceled"].includes(so.status) &&
          so.cash_register_id == this.relevantCashRegister.id
      );
    },
  },
};
</script>
